import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { HpButton, HpCardHeader, HpTag, TableScroll } from '@hp/ui';
import { useMe } from '../../api/me';
import { StatusBadge } from '../../components/badges';
import { ErrorAlert } from '../../components/error-alert';
import { PdLink } from '../../components/pd-link';
import { RelativeTime } from '../../components/relative-time';
import { Disclosure } from '../../components/ui/disclosure';
import { Spinner } from '../../components/ui/icons';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { display, fmtDateTime, fmtInt, prettyJson } from '../../lib/format';
import { useLeadErrorText, useNeedsRuleText } from './error-text';
import { actionFor, type LeadAction } from './labels';
import {
  IN_FLIGHT_STATUSES,
  isInvalidReason,
  isLeadStatus,
  isOwnerSource,
  isTraceResult,
  isTraceStep,
  LEAD_STATUS_TONE,
} from './labels';
import {
  useOwners,
  usePipelines,
  useReceivedLead,
  type ReceivedDetail,
  type ReceivedLead,
  type TraceStep,
} from './queries';

const inFlight = (status: string) => isLeadStatus(status) && IN_FLIGHT_STATUSES.includes(status);

/** Status tag of a lead (lead statuses have their own tones, not the run tones of `statusTone`). */
export function LeadStatusTag({ status }: { status: string }) {
  const { t } = useTranslation(['leads']);
  return isLeadStatus(status) ? (
    <HpTag tone={LEAD_STATUS_TONE[status]}>{t(`general.leadStatus.${status}`)}</HpTag>
  ) : (
    <HpTag tone="neutral">{status}</HpTag>
  );
}

/** Names for the ids a trace carries: owners and pipelines/stages (active ones; others stay ids). */
function useTraceNames() {
  const { t } = useTranslation(['leads']);
  const owners = useOwners();
  const pipelines = usePipelines();
  const owner = (id: unknown) => {
    const name = owners.data?.find((o) => o.pdId === id)?.name;
    return name ? `${name} (#${display(id)})` : display(id);
  };
  const pipelineName = (id: unknown) => pipelines.data?.find((p) => p.pdId === id)?.name;
  const stageName = (id: unknown) => pipelines.data?.flatMap((p) => p.stages).find((s) => s.pdId === id)?.name;
  return (key: string, value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? t('received.trace.yes') : t('received.trace.no');
    if (key === 'userPdId' || key === 'ownerUserPdId' || key === 'fallbackInactive') return owner(value);
    if ((key === 'pool' || key === 'marketPool') && Array.isArray(value)) {
      return value.length === 0 ? t('received.trace.emptyPool') : value.map(owner).join(', ');
    }
    if (key === 'pipelinePdId') {
      const name = pipelineName(value);
      return name ? `${name} (#${display(value)})` : display(value);
    }
    if (key === 'stagePdId') {
      const name = stageName(value);
      return name ? `${name} (#${display(value)})` : display(value);
    }
    return display(value);
  };
}

/** The decision trace: one numbered step per cascade step, its result and the facts it used. */
export function TraceList({ trace }: { trace: readonly TraceStep[] }) {
  const { t } = useTranslation(['leads']);
  const valueOf = useTraceNames();
  if (trace.length === 0) return <p className="bo-muted">{t('received.trace.empty')}</p>;
  return (
    <ol className="bo-trace" aria-label={t('received.trace.aria')}>
      {trace.map((s, i) => (
        <li key={`${s.step}-${i}`} className="bo-trace__step">
          <span className="bo-row">
            <span className="bo-strong">{isTraceStep(s.step) ? t(`received.trace.step.${s.step}`) : s.step}</span>
            <HpTag tone="neutral">{isTraceResult(s.result) ? t(`received.trace.result.${s.result}`) : s.result}</HpTag>
          </span>
          {s.detail && Object.keys(s.detail).length > 0 && (
            <dl className="bo-kv bo-kv--compact">
              {Object.entries(s.detail).map(([key, value]) => (
                <div key={key}>
                  <dt className="bo-mono">{key}</dt>
                  <dd>{valueOf(key, value)}</dd>
                </div>
              ))}
            </dl>
          )}
        </li>
      ))}
    </ol>
  );
}

/** A labelled value line of a detail section; an empty value shows a dash. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  const empty = children === null || children === undefined || children === false || children === '';
  return (
    <div>
      <dt>{label}</dt>
      <dd>{empty ? <span className="bo-muted">—</span> : children}</dd>
    </div>
  );
}

function Section({ title, wide = false, children }: { title: string; wide?: boolean; children: ReactNode }) {
  return (
    <fieldset className={wide ? 'bo-fieldset bo-lead-detail__wide' : 'bo-fieldset'}>
      <legend>{title}</legend>
      {children}
    </fieldset>
  );
}

/** Routing facts of a lead: the rule it used, the owner and why, the target, the frozen option ids. */
function RoutingFacts({ lead }: { lead: ReceivedDetail }) {
  const { t } = useTranslation(['leads']);
  const needsRuleText = useNeedsRuleText();
  const outcome = lead.decision?.outcome;
  const rule = lead.currentRule;
  let ruleText: string | null = null;
  if (lead.ruleId !== null) {
    const version = lead.ruleVersion ?? 0;
    if (!rule) ruleText = t('received.detail.ruleDeleted', { version });
    else if (rule.kind === 'campaign')
      ruleText = t('received.detail.ruleCampaign', {
        name: rule.campaignName ?? t('rules.unknownId', { id: rule.id }),
        version,
      });
    else ruleText = t('received.detail.ruleDefault', { market: rule.marketCode ?? lead.marketCode, version });
  }
  const ruleMoved = rule !== null && lead.ruleVersion !== null && rule.version !== lead.ruleVersion;
  return (
    <dl className="bo-kv">
      <Fact label={t('received.detail.rule')}>
        {ruleText && (
          <>
            {ruleText}
            {ruleMoved && (
              <span className="bo-muted"> · {t('received.detail.ruleNow', { version: rule.version })}</span>
            )}
          </>
        )}
      </Fact>
      {outcome?.kind === 'routed' && (
        <Fact label={t('received.detail.ownerSource')}>
          {isOwnerSource(outcome.ownerSource) ? t(`general.ownerSource.${outcome.ownerSource}`) : outcome.ownerSource}
        </Fact>
      )}
      <Fact label={t('received.detail.owner')}>
        {lead.ownerUserPdId !== null && (lead.ownerName ?? t('rules.unknownId', { id: lead.ownerUserPdId }))}
      </Fact>
      <Fact label={t('received.detail.target')}>
        {lead.pipelineName && (
          <>
            {lead.pipelineName}
            {lead.stageName && ` → ${lead.stageName}`}
          </>
        )}
      </Fact>
      {outcome?.kind === 'routed' && (
        <>
          <Fact label={t('editor.field.campaignOption')}>
            {outcome.campaignOptionId !== null && <span className="bo-mono">#{outcome.campaignOptionId}</span>}
          </Fact>
          <Fact label={t('editor.field.projectOptionId')}>
            {outcome.projectOptionId !== null && <span className="bo-mono">#{outcome.projectOptionId}</span>}
          </Fact>
        </>
      )}
      <Fact label={t('received.detail.settingsVersion')}>
        {lead.settingsVersion !== null && fmtInt(lead.settingsVersion)}
      </Fact>
      {lead.status === 'needs_rule' && <Fact label={t('received.detail.reason')}>{needsRuleText(lead.error)}</Fact>}
      {lead.status === 'invalid' && (
        <Fact label={t('received.detail.reason')}>
          {isInvalidReason(lead.invalidReason) ? t(`received.invalidReason.${lead.invalidReason}`) : lead.invalidReason}
        </Fact>
      )}
      {lead.status !== 'needs_rule' && lead.error && (
        <Fact label={t('received.detail.error')}>
          <span className="bo-hint--danger">{lead.error}</span>
        </Fact>
      )}
      {lead.notBefore && (
        <Fact label={t('received.detail.nextAttempt')}>
          <RelativeTime value={lead.notBefore} />
        </Fact>
      )}
      {lead.routedByDefault && (
        <div>
          <dt />
          <dd className="bo-hint">{t('received.detail.byDefault')}</dd>
        </div>
      )}
    </dl>
  );
}

function RunsTable({ runs }: { runs: ReceivedDetail['runs'] }) {
  const { t } = useTranslation(['leads', 'common']);
  if (runs.length === 0) return <p className="bo-muted">{t('received.detail.noRuns')}</p>;
  return (
    <TableScroll label={t('received.detail.runs')}>
      <BrutTable>
        <BrutTheadRow>
          <th>{t('received.detail.runColumns.status')}</th>
          <th className="bo-num">{t('received.detail.runColumns.attempts')}</th>
          <th>{t('received.detail.runColumns.started')}</th>
          <th>{t('received.detail.runColumns.finished')}</th>
          <th>{t('received.detail.runColumns.error')}</th>
        </BrutTheadRow>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id}>
              <td>
                <StatusBadge status={run.status} />
              </td>
              <td className="bo-num">{fmtInt(run.attempts)}</td>
              <td>
                <RelativeTime value={run.startedAt} />
              </td>
              <td>
                <RelativeTime value={run.finishedAt} />
              </td>
              <td>
                {run.error ? <span className="bo-hint--danger">{run.error}</span> : <span className="bo-muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </BrutTable>
    </TableScroll>
  );
}

/**
 * The detail panel of one received lead (an `hp-card` above the table, like the rule editor): contact,
 * routing, the decision trace, answers, the Pipedrive records, the delivery runs and — for managers —
 * the raw event. Esc and Close close it.
 */
export function LeadDetail({
  id,
  canManage,
  busy,
  onAction,
  onClose,
}: {
  id: string;
  canManage: boolean;
  /** An action on this lead is running. */
  busy: boolean;
  onAction: (lead: ReceivedLead, action: LeadAction) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['leads', 'common']);
  const describe = useLeadErrorText();
  const query = useReceivedLead(id, inFlight);
  const pdDomain = useMe().data?.pipedriveCompanyDomain;
  const navigate = useNavigate();
  const cardRef = useRef<HTMLElement>(null);
  const lead = query.data;

  // Bring the panel into view and onto the keyboard when it opens (also from a deep link).
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    card.scrollIntoView({ block: 'start', behavior: 'smooth' });
    card.focus({ preventScroll: true });
  }, [id]);

  // Esc closes the panel, unless a confirmation dialog is open (Esc belongs to the dialog then).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('dialog[open]')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const action = lead && canManage ? actionFor(lead.status) : null;
  const title = lead ? (lead.name ?? t('received.unnamed')) : t('received.detail.loading');
  const answers = lead?.answers ? Object.entries(lead.answers) : [];

  return (
    <section className="hp-card" ref={cardRef} tabIndex={-1} aria-label={title}>
      <HpCardHeader
        title={title}
        icon="user"
        subtitle={
          lead ? (
            <span className="bo-mono">{t('received.detail.ids', { id: lead.id, channel: lead.channelLabel })}</span>
          ) : undefined
        }
      >
        {lead && <LeadStatusTag status={lead.status} />}
        {lead?.routedByDefault && (
          <HpTag tone="warning" title={t('received.defaultTitle')}>
            {t('received.defaultChip')}
          </HpTag>
        )}
        {lead?.isTest && (
          <HpTag tone="info" title={t('received.testTitle')}>
            {t('received.testChip')}
          </HpTag>
        )}
        {lead && action && (
          <HpButton
            variant="secondary"
            icon={action === 'retry' ? 'refresh' : 'rotateCcw'}
            busy={busy}
            onClick={() => onAction(lead, action)}
          >
            {t(`received.${action}`)}
          </HpButton>
        )}
        {lead && canManage && lead.adCampaignId !== null && (
          <HpButton
            variant="secondary"
            icon="edit"
            onClick={() => void navigate(`/marketing/leads?tab=rules&campaign=${lead.adCampaignId}`)}
          >
            {t('received.detail.openRule')}
          </HpButton>
        )}
        <HpButton variant="quiet" icon="close" onClick={onClose}>
          {t('common:actions.close')}
        </HpButton>
      </HpCardHeader>
      <ErrorAlert error={query.error} describe={describe} className="bo-alert--inset" />
      {query.isLoading && (
        <p className="hp-card__body">
          <Spinner>{t('common:actions.loading')}</Spinner>
        </p>
      )}
      {lead && (
        <div className="hp-card__body bo-lead-detail">
          <Section title={t('received.detail.contact')}>
            <dl className="bo-kv">
              <Fact label={t('received.detail.name')}>{lead.name}</Fact>
              <Fact label={t('received.detail.email')}>{lead.email}</Fact>
              <Fact label={t('received.detail.phone')}>
                {lead.phone && <span className="bo-mono">{lead.phone}</span>}
              </Fact>
              <Fact label={t('received.detail.campaign')}>{lead.campaignName}</Fact>
              <Fact label={t('received.detail.received')}>
                <span title={fmtDateTime(lead.receivedAt)}>{fmtDateTime(lead.receivedAt)}</span>
              </Fact>
              <Fact label={t('received.detail.deliveredAt')}>{lead.deliveredAt && fmtDateTime(lead.deliveredAt)}</Fact>
            </dl>
            {lead.message && <p className="bo-lead-detail__message">{lead.message}</p>}
          </Section>

          <Section title={t('received.detail.routing')}>
            <RoutingFacts lead={lead} />
          </Section>

          <Section title={t('received.detail.trace')} wide>
            {lead.decision ? (
              <TraceList trace={lead.decision.trace} />
            ) : (
              <p className="bo-muted">{t('received.trace.empty')}</p>
            )}
          </Section>

          <Section title={t('received.detail.answers')}>
            {answers.length > 0 ? (
              <dl className="bo-kv">
                {answers.map(([key, value]) => (
                  <Fact key={key} label={key}>
                    {value}
                  </Fact>
                ))}
              </dl>
            ) : (
              <p className="bo-muted">{t('received.detail.noAnswers')}</p>
            )}
          </Section>

          <Section title={t('received.detail.pipedrive')}>
            <dl className="bo-kv">
              <Fact label={t('received.detail.person')}>
                {lead.personPdId !== null && <PdLink domain={pdDomain} entity="person" pdId={lead.personPdId} />}
              </Fact>
              <Fact label={t('received.detail.deal')}>
                {lead.dealPdId !== null && <PdLink domain={pdDomain} entity="deal" pdId={lead.dealPdId} />}
              </Fact>
              <Fact label={t('received.detail.note')}>
                {lead.notePdId !== null && <span className="bo-mono">#{lead.notePdId}</span>}
              </Fact>
              <Fact label={t('received.detail.activity')}>
                {lead.activityPdId !== null && <span className="bo-mono">#{lead.activityPdId}</span>}
              </Fact>
            </dl>
            {lead.isTest && <p className="bo-hint">{t('received.detail.testNeverDelivered')}</p>}
          </Section>

          <Section title={t('received.detail.runs')} wide>
            <RunsTable runs={lead.runs} />
          </Section>

          {canManage && (lead.payload !== null || lead.headers !== null) && (
            <div className="bo-lead-detail__wide">
              <Disclosure title={t('received.detail.raw')}>
                <div className="bo-sections">
                  {lead.headers !== null && (
                    <>
                      <p className="bo-subform__title">{t('received.detail.headers')}</p>
                      <pre className="bo-code">{prettyJson(lead.headers)}</pre>
                    </>
                  )}
                  <p className="bo-subform__title">{t('received.detail.payload')}</p>
                  <pre className="bo-code">{prettyJson(lead.payload)}</pre>
                </div>
              </Disclosure>
            </div>
          )}
          {canManage && lead.sourceDetail !== null && lead.sourceDetail !== undefined && (
            <div className="bo-lead-detail__wide">
              <Disclosure title={t('received.detail.source')}>
                <pre className="bo-code">{prettyJson(lead.sourceDetail)}</pre>
              </Disclosure>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
